import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { useApp } from "../../lib/store";
import { motion } from "framer-motion";
import { Users, ShieldCheck, MailQuestion, KeyRound, Factory, Building2, Shuffle, ScrollText, Lock } from "lucide-react";

const TILES = [
  { key: "total_users", label: "iam_total_users", icon: Users, to: "/iam/users" },
  { key: "active_30d", label: "iam_active_30d", icon: ShieldCheck, to: "/iam/users" },
  { key: "pending_invitations", label: "iam_pending_invites", icon: MailQuestion, to: "/iam/users?status=invited", accent: true },
  { key: "pending_scope", label: "iam_pending_scope", icon: KeyRound, to: "/iam/users?pending_scope=1", accent: true },
  { key: "failed_logins_7d", label: "iam_failed_logins", icon: Lock, to: "/iam/auth", accent: true },
  { key: "reassignment_open", label: "iam_reassign_open", icon: Shuffle, to: "/iam/users", accent: true },
];

const LINKS = [
  { to: "/iam/users", label: "iam_users", icon: Users },
  { to: "/iam/roles", label: "iam_roles", icon: ShieldCheck },
  { to: "/iam/org", label: "iam_org", icon: Factory },
  { to: "/iam/auth", label: "iam_auth", icon: Lock },
  { to: "/iam/audit", label: "iam_audit", icon: ScrollText },
];

export default function IamDashboard() {
  const { t } = useApp();
  const [d, setD] = useState(null);

  useEffect(() => { api.get("/iam/dashboard").then((r) => setD(r.data)).catch(() => {}); }, []);

  return (
    <div className="space-y-6" data-testid="iam-dashboard">
      <div className="relative overflow-hidden rounded-2xl bg-[#1B2A4A] grain p-6 sm:p-8">
        <div className="absolute -right-10 -top-10 h-52 w-52 rounded-full bg-[#E8871E]/25 blur-3xl animate-float-slow" />
        <div className="relative">
          <p className="text-[#E8871E] text-xs font-semibold uppercase tracking-[0.2em]">{t("nav_group_administration")}</p>
          <h1 className="text-white text-3xl sm:text-4xl font-bold tracking-tight mt-1" style={{ fontFamily: "Work Sans" }}>
            {t("iam_dashboard")}
          </h1>
          <p className="text-white/60 text-sm mt-2 max-w-xl">{t("iam_dashboard_sub")}</p>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
        {TILES.map((tile, i) => {
          const Icon = tile.icon;
          const val = d?.[tile.key];
          return (
            <motion.div key={tile.key} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, delay: i * 0.05 }}>
              <Link to={tile.to} data-testid={`iam-tile-${tile.key}`}
                className="block rounded-xl border border-gray-200 bg-white p-4 card-hover">
                <div className="flex items-center justify-between">
                  <Icon className={`h-4 w-4 ${tile.accent && val ? "text-[#E8871E]" : "text-gray-400"}`} />
                  <span className={`text-2xl font-bold ${tile.accent && val ? "text-[#E8871E]" : "text-[#1B2A4A]"}`}>
                    {val ?? "–"}
                  </span>
                </div>
                <p className="text-[11px] text-gray-500 mt-2 leading-snug">{t(tile.label)}</p>
              </Link>
            </motion.div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        {LINKS.map((l) => {
          const Icon = l.icon;
          return (
            <Link key={l.to} to={l.to} data-testid={`iam-link-${l.label}`}
              className="rounded-xl border border-gray-200 bg-white p-4 flex items-center gap-3 card-hover">
              <span className="h-9 w-9 rounded-lg bg-[#1B2A4A] flex items-center justify-center">
                <Icon className="h-4 w-4 text-[#E8871E]" />
              </span>
              <span className="text-sm font-semibold text-[#1B2A4A]">{t(l.label)}</span>
            </Link>
          );
        })}
      </div>

      {d && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
          {[["iam_plants", d.plants, Factory], ["accounts", d.accounts, Building2],
            ["iam_roles_count", d.roles, ShieldCheck], ["iam_sso_users", d.sso_users, Lock]].map(([k, v, Icon]) => (
            <div key={k} className="rounded-lg border border-gray-100 bg-white px-3 py-2.5 flex items-center gap-2"
              data-testid={`iam-stat-${k}`}>
              <Icon className="h-3.5 w-3.5 text-gray-400" />
              <span className="text-gray-500">{t(k)}</span>
              <span className="ml-auto font-bold text-[#1B2A4A]">{v}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
